import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Input from 'components/UI/Input';
import ConnectionStatusBadge from 'components/Company/ConnectionStatusBadge';
import CreditActionButtons from 'components/Company/CreditActionButtons';
import { Space } from 'styles/themeSpaces';
import { Text, Border, Background } from 'styles/themeColors';
import { 
  MDMCompanyCardProps, 
  CreditTrackingMode 
} from 'types/credit.types';
import { 
  CREDIT_LABELS, 
  CREDIT_DESCRIPTIONS, 
  CREDIT_ERROR_MESSAGES,
  CREDIT_PLACEHOLDERS,
  CREDIT_STATUS_MESSAGES
} from 'constants/credit';
import { 
  getConnectionStatusConfig,
  getAddressFromCreditInfo 
} from 'utils/credit';

const TextSecondary = styled.span`
  color: ${Text.secondary} !important;
  margin-top: ${Space.spBasePlus};
`;

const CompanyInfoRow = styled(FlexboxContainer)`
  margin-bottom: ${Space.spBase};
  opacity: 0.7;
`;

const LabelColumn = styled.div`
  width: 144px;
  color: ${Text.secondary};
  padding-right: ${Space.spBase};
`;

const ValueColumn = styled(FlexboxContainer)`
  flex: 1;
  color: ${Text.secondary};
`;

const MDMCompanyCard: React.FC<MDMCompanyCardProps> = ({
  className,
  mdmId,
  isConnected = false,
  isInvalid,
  setIsInvalid,
  onApplyMDM,
  creditTrackingMode,
  creditInfo,
  error
}) => {
  const [value, setValue] = useState(mdmId);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
      setValue(mdmId);
  }, [mdmId]);

  const errorMessage = isInvalid
    ? error ? error : CREDIT_ERROR_MESSAGES.MDM_INCORRECT
    : CREDIT_ERROR_MESSAGES.ID_REQUIRED;

  // Get connection status configuration
  const connectionConfig = getConnectionStatusConfig(
    creditTrackingMode as CreditTrackingMode, 
    creditInfo
  );

  const onApplyClick = () => {
    setDirty(true);

    if (value) {
      onApplyMDM(value);
    }
  };

  const onCancelClick = () => {
    setValue(mdmId);
    setIsInvalid(false);
    setDirty(false);
  };

  const onInputChange = (value: string) => {
    if (!isNaN(Number(value)) && value.trim() !== '') {
      setIsInvalid(false);
      setValue(value);
    }

    if (value.trim() === '') {
      setValue(value);
    }
    
    setDirty(true);
  };

  // Get formatted address lines
  const { line1, line2, line3 } = getAddressFromCreditInfo(creditInfo);

  return (
    <FlexboxContainer
      className={`border ${className}`}
      width="50%"
      flexDirection="column"
      padding={Space.sp2x}
      borderColor={`${Border.light} !important`}
      bgColor={Background.light}
    >
      <h5>{CREDIT_LABELS.COMPANY_UNIQUE_ID}</h5>
      <TextSecondary className="dense-italic">
        {CREDIT_DESCRIPTIONS.MDM_DESCRIPTION}
      </TextSecondary>

      <FlexboxContainer
        flexDirection="column"
        margin={`${Space.spBasePlus} 0`}
      >
        <div className="base" style={{ marginBottom: Space.spBase }}>{CREDIT_LABELS.MDM_ID}</div>
        <FlexboxContainer 
          justifyContent="space-between" 
          alignItems="baseline"
          gap={Space.spBase}
        >
          <Input
            id="mdmID"
            placeholder={isInvalid ? "" : CREDIT_PLACEHOLDERS.ENTER_NUMBER}
            value={value}
            errorMessage={errorMessage}
            dirty={dirty}
            isInvalid={isInvalid}
            onChange={e => onInputChange(e.target.value)}
            required
            width="100%"
            maxLength='24'
          />
          <CreditActionButtons
            onCancel={onCancelClick}
            onApply={onApplyClick}
            disabled={!dirty}
          />
        </FlexboxContainer>
      </FlexboxContainer>

      {isConnected && [CreditTrackingMode.ERP,CreditTrackingMode.OFFLINE].includes(creditTrackingMode!) ? (
        <FlexboxContainer
          className="border"
          flexDirection="column"
          bgColor={Background.shaded}
          borderColor={`${Border.light} !important`}
          margin={`${Space.spBasePlus} 0`}
          padding={Space.spBase}
        >
          <FlexboxContainer
            alignItems="center"
            margin={`0 0 ${Space.spBase} 0`}
          >
            <ConnectionStatusBadge
              status={connectionConfig.status}
              backgroundColor={connectionConfig.backgroundColor}
              color={connectionConfig.color}
            />
          </FlexboxContainer>

          <FlexboxContainer flexDirection="column" gap={Space.spBase}>
            <CompanyInfoRow>
              <LabelColumn className="base-italic">
                {CREDIT_LABELS.COMPANY_NAME}
              </LabelColumn>
              <ValueColumn className="base-italic">
                {creditInfo?.companyMdmDetails?.name || CREDIT_STATUS_MESSAGES.NOT_AVAILABLE}
              </ValueColumn>
            </CompanyInfoRow>

            <CompanyInfoRow alignItems="flex-start">
              <LabelColumn className="base-italic">
                {CREDIT_LABELS.ADDRESS}
              </LabelColumn>
              <ValueColumn flexDirection="column">
                {line1 && <div className="base-italic">{line1}</div>}
                {line2 && <div className="base-italic">{line2}</div>}
                {line3 && <div className="base-italic">{line3}</div>}
              </ValueColumn>
            </CompanyInfoRow>

            <CompanyInfoRow>
              <LabelColumn className="base-italic">
                {CREDIT_LABELS.ACCOUNT_TYPE}
              </LabelColumn>
              <ValueColumn className="base-italic">
                {creditInfo?.companyMdmDetails?.accountType || CREDIT_STATUS_MESSAGES.NOT_AVAILABLE}
              </ValueColumn>
            </CompanyInfoRow>

            <CompanyInfoRow>
              <LabelColumn className="base-italic">
                {CREDIT_LABELS.CREDIT_DATA}
              </LabelColumn>
              <ValueColumn className="base-italic">
                {creditInfo?.companyMdmDetails?.creditData || CREDIT_STATUS_MESSAGES.NOT_AVAILABLE}
              </ValueColumn>
            </CompanyInfoRow>

            <CompanyInfoRow>
              <LabelColumn className="base-italic">
                {CREDIT_LABELS.ID_STATUS}
              </LabelColumn>
              <ValueColumn className="base-italic">
                {creditInfo?.companyMdmDetails?.idStatus || CREDIT_STATUS_MESSAGES.NOT_AVAILABLE}
              </ValueColumn>
            </CompanyInfoRow>
          </FlexboxContainer>
        </FlexboxContainer>
      ) : null}
    </FlexboxContainer>
  );
};


export default MDMCompanyCard;